import { Form, Segmented, Select } from "antd"
import dayjs, { Dayjs } from "dayjs"

import { CustomRangePicker } from "@/components/_Shared/CustomRangePicker"
import classes from "@/components/Analytics/AnalyticsSettings/AnalyticsFiltersSettings.module.scss"
import { useAnalyticsHelpers } from "@/components/Analytics/useAnalyticsHelpers"

import { IPagingResponse } from "@/types/api"
import { AnalyticsPeriod, AnalyticsFilterPeriods, IAnalyticsSettingsOptions, AnalyticsOptionValue, FilterType } from "@/types/IAnalytics"
import { IDepot } from "@/types/IDepot"
import { IFuelType } from "@/types/IFuelType"
import { ISelectListItem } from "@/types/ui"

interface AnalyticsFiltersSettingsProps {
  fuelTypes: IPagingResponse<IFuelType> | undefined
  depots: IDepot[] | undefined
  customerClients: ISelectListItem[] | undefined
  customerCategories: ISelectListItem[] | undefined
  options: IAnalyticsSettingsOptions
  onRangeChange: ((dates: [start: Dayjs | null, end: Dayjs | null] | null, dateStrings: [string, string]) => void) | undefined
  onPeriodChange: ((period: AnalyticsPeriod) => void) | undefined
  onChosenFuelTypeChange: ((fuelTypeId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  onChosenDepotChange: ((depotId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  onCustomerClientChange: ((customerClientId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  onCustomerCategoryChange: ((categoryId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => void) | undefined
  filterOptions?: FilterType[] | undefined
}

const AnalyticsFiltersSettings = (props: AnalyticsFiltersSettingsProps) => {
  const {
    fuelTypes,
    depots,
    customerClients,
    customerCategories,
    options,
    filterOptions,
    onRangeChange,
    onPeriodChange,
    onChosenFuelTypeChange,
    onChosenDepotChange,
    onCustomerClientChange,
    onCustomerCategoryChange
  } = props

  const {
    startDate,
    endDate,
    activePeriod
  } = options

  const { isFilterTypeSetOn } = useAnalyticsHelpers({})

  return (
    <Form layout="vertical" size="large">
      {isFilterTypeSetOn(filterOptions, FilterType.Period) &&
        <>
          <Form.Item>
            <CustomRangePicker
              value={[startDate ? dayjs(startDate) : undefined, endDate ? dayjs(endDate) : undefined]}
              onChange={onRangeChange}
            />
          </Form.Item>
          <Form.Item>
            <Segmented
              options={AnalyticsFilterPeriods}
              value={activePeriod}
              onChange={onPeriodChange}
            />
          </Form.Item>
        </>
      }
      {isFilterTypeSetOn(filterOptions, FilterType.Fuel) &&
        <Form.Item
          className={classes["mb-8"]}
          label="Вид топлива">
          <Select
            onClear={() => onChosenFuelTypeChange && onChosenFuelTypeChange(undefined, [])}
            onChange={(value, option) => onChosenFuelTypeChange && onChosenFuelTypeChange(value, option)}
            value={options.fuelTypeId}
            placeholder="Вид топлива"
            showSearch
            allowClear
            options={fuelTypes?.response.data.map(d => ({
              value: d.id,
              label: d.name
            }))}
          />
        </Form.Item>
      }
      {isFilterTypeSetOn(filterOptions, FilterType.Depot) &&
        <Form.Item
          className={classes["mb-10"]}
          label="Нефтебаза">
          <Select
            onClear={() => onChosenDepotChange && onChosenDepotChange(undefined, [])}
            onChange={(value, option) => onChosenDepotChange && onChosenDepotChange(value, option)}
            value={options.depotId}
            placeholder="Нефтебаза"
            showSearch
            allowClear
            disabled={options.depotDisabled === true}
            options={depots?.map(d => ({
              value: d.id,
              label: d.name
            }))}
          />
        </Form.Item>
      }
      {isFilterTypeSetOn(filterOptions, FilterType.Customer) &&
        <Form.Item
          label="Клиент">
          <Select
            onClear={() => onCustomerClientChange && onCustomerClientChange(undefined, [])}
            onChange={(value, option) => onCustomerClientChange && onCustomerClientChange(value, option)}
            value={options.customerClientId}
            placeholder="Клиент"
            showSearch
            allowClear
            options={customerClients}
          />
        </Form.Item>
      }
      {isFilterTypeSetOn(filterOptions, FilterType.CustomerCategory) &&
        <Form.Item
          label="Категория клиентов">
          <Select
            onClear={() => onCustomerCategoryChange && onCustomerCategoryChange(undefined, [])}
            onChange={(value, option) => onCustomerCategoryChange && onCustomerCategoryChange(value, option)}
            value={options.customerCategoryId}
            placeholder="Категория клиентов"
            showSearch
            allowClear
            options={customerCategories}
          />
        </Form.Item>
      }
    </Form>
  )
}

export default AnalyticsFiltersSettings